import { CallButton } from "@/components/prospects/CallButton";
import { PointageButtons } from "@/components/prospects/PointageButtons";
import { WhatsAppButton } from "@/components/prospects/WhatsAppButton";
import { dayMonth, rdvTimes } from "@/lib/brief";
import { TONE_STYLES } from "@/lib/colors";
import { cn, fullName } from "@/lib/utils";
import type { CallStatsEntry } from "@shared/call-stats";
import type { Prospect } from "@shared/schema";
import { bookedAtOf } from "@shared/whatsapp";

export type PeopleKind = "absents" | "aPointer" | "presents";

function Pill({ tone, children }: { tone: "green" | "coral"; children: React.ReactNode }) {
  return <span className={cn("pill", TONE_STYLES[tone].pillBg, TONE_STYLES[tone].pillFg)}>{children}</span>;
}

/** Liste de personnes d'un dépliant (bandeau d'Aujourd'hui, page Statistiques). */
export function StatsPeopleList({
  kind,
  entries,
  bookingUrl,
  onPointed,
  className,
}: {
  kind: PeopleKind;
  entries: CallStatsEntry[];
  /** Requis pour kind="absents" (bouton WhatsApp). */
  bookingUrl?: string;
  onPointed?: (eventId: number, prospect: Prospect, outcome: "attended" | "no_show") => void;
  className?: string;
}) {
  if (entries.length === 0) {
    return (
      <p className="py-2 text-xs text-[var(--color-muted-foreground)]">Personne pour l'instant.</p>
    );
  }
  return (
    <ul className={cn("divide-y divide-[var(--color-border)]", className)}>
      {entries.map((e) => {
        const { prospect, event } = e;
        const t = rdvTimes(event.startTime);
        const when = t ? `${t.parisDay} · ${t.paris} Paris` : "";
        const next =
          prospect.nextAction
            ? `${prospect.nextAction}${dayMonth(prospect.nextFollowUpAt) ? ` le ${dayMonth(prospect.nextFollowUpAt)}` : ""}`
            : null;
        const sub =
          kind === "absents"
            ? [`Absent le ${when}`, next].filter(Boolean).join(" · ")
            : kind === "aPointer"
              ? `RDV le ${when}`
              : `Présent le ${when}`;
        return (
          <li
            key={event.id}
            className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 py-2.5"
          >
            <span className="min-w-0 flex-1 basis-48">
              <span className="flex flex-wrap items-center gap-2">
                <b className="truncate text-sm text-[var(--color-navy-700)]">{fullName(prospect)}</b>
                {kind === "absents" && e.rebookedAt && <Pill tone="green">a repris RDV</Pill>}
                {kind === "presents" && e.closed && <Pill tone="green">closé</Pill>}
              </span>
              <span className="block truncate text-xs text-[var(--color-muted-foreground)]">{sub}</span>
            </span>
            <span className="flex flex-wrap items-center gap-2">
              {kind === "aPointer" ? (
                <>
                  <PointageButtons
                    prospectId={prospect.id}
                    eventId={event.id}
                    eventStart={event.startTime}
                    onDone={(p, o) => onPointed?.(event.id, p, o)}
                  />
                  <CallButton phone={prospect.phone} variant="icon" />
                </>
              ) : kind === "absents" && !e.rebookedAt ? (
                <>
                  <CallButton phone={prospect.phone} variant="icon" />
                  <WhatsAppButton
                    prospect={prospect}
                    template="absence"
                    rdvStart={event.startTime}
                    bookedAt={bookedAtOf(event.rawJson)}
                    bookingUrl={bookingUrl ?? ""}
                    size="sm"
                    variant="icon"
                  />
                </>
              ) : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
